import React, { useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import Tag from '@wix/da-ds/pkg/TagList/Tag';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { MAX_TITLE_LENGTH } from '../../../../constants';
import { OriginalDeviationFieldName } from '../../types';

import s from './TitleField.scss';

interface Props {}

const TitleField: React.FC<Props> = () => {
  const { t } = useTranslation();
  const id = useId();
  const { control, errors, setValue } = useFormContext();
  const {
    field: { value, onChange },
  } = useController({ name: OriginalDeviationFieldName.TITLE, control });
  const error = errors[OriginalDeviationFieldName.TITLE];

  const ideas: { tag: string, color }[] = [
    { tag: 'video', color: 'blue' },
    { tag: 'ych', color: 'pink' },
    { tag: 'character', color: 'purple' },
    { tag: 'mysteryBox', color: 'orange' },
    { tag: 'adoptsSheet', color: 'gold' },
    { tag: 'avatar', color: 'green' },
    { tag: 'outfit', color: 'light-blue' },
  ];

  const setTitle = useCallback(tag => {
    setValue(
      OriginalDeviationFieldName.TITLE,
      t(`originalDeviationPopup.form.ideas.tag.var.${tag}`)
    );
  }, [setValue, t]);

  return (
    <>
      <TextInput
        id={id}
        maxLength={MAX_TITLE_LENGTH}
        label={t('originalDeviationPopup.form.title.label')}
        placeholder={t('originalDeviationPopup.form.title.placeholder')}
        value={value}
        onChange={onChange}
        details={error?.message}
        validationResult={error ? 'error' : undefined}
        autoFocus
        isRequired
        noBottomSpacing
      />
      {!value && (
        <div className={s['ideas']}>
          <Heading className={s['ideas-heading']}>
            {t('originalDeviationPopup.form.ideas.title')}
          </Heading>
          <div className={s['ideas-tags']}>
            {ideas.map(({ tag, color }) => (
              <Tag
                key={`idea-tag-${tag}`}
                size="small"
                className={s['ideas-tag']}
                ideaBackground={color}
                onClick={() => setTitle(tag)}
              >
                {t(`originalDeviationPopup.form.ideas.tag.var.${tag}`)}
              </Tag>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
TitleField.displayName = 'TitleField';

export default TitleField;
